import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/use-page-title";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useSmallBusinessDashboard, QUERY_KEYS } from "@/hooks/use-queries";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import {
  Users,
  DollarSign,
  Building2,
  Copy,
  Plus,
  ArrowRight,
  Check,
  X,
  Send,
  Wallet,
  Heart,
  LogOut,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";

interface PartnerData {
  companyName: string;
  companyCode: string;
  monthlyPerEmployeeCents: number;
}

interface StatsData {
  enrolledEmployees: number;
  invitedEmployees: number;
  totalEmployees: number;
  totalContributed: number;
  thisMonthContributed: number;
  monthlyPerEmployeeCents: number;
  estimatedMonthlyCost: number;
}

interface EmployeeData {
  id: string;
  employeeName: string;
  employeeEmail: string;
  department: string;
  status: string;
  walletId: string | null;
  totalReceivedCents: number;
  enrolledAt: string | null;
  createdAt: string;
}

interface ContributionData {
  id: string;
  employeeId: string;
  walletId: string;
  type: string;
  amountCents: number;
  description: string;
  createdAt: string;
}

interface DashboardData {
  partner: PartnerData;
  stats: StatsData;
  employees: EmployeeData[];
  recentContributions: ContributionData[];
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function SmallBusinessDashboard() {
  usePageTitle("Small Business Dashboard", "Manage your small business employee health benefits, wallet contributions, and enrollment.");
  const { toast } = useToast();
  const { logoutMutation } = useAuth();
  const [, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState("overview");
  const [codeCopied, setCodeCopied] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isFundAllOpen, setIsFundAllOpen] = useState(false);
  const [isFundEmployeeOpen, setIsFundEmployeeOpen] = useState(false);
  const [fundEmployeeId, setFundEmployeeId] = useState<string | null>(null);
  const [fundAmountDollars, setFundAmountDollars] = useState("");
  const [inviteRows, setInviteRows] = useState([
    { name: "", email: "" },
    { name: "", email: "" },
    { name: "", email: "" },
  ]);

  const { data, isLoading } = useSmallBusinessDashboard();

  const fundAllMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/small-business/fund-all", {
        amountCents: data?.partner.monthlyPerEmployeeCents,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.smallBusinessDashboard });
      setIsFundAllOpen(false);
      toast({
        title: "Funding Complete",
        description: "All enrolled employees have been funded.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to fund employees",
        variant: "destructive",
      });
    },
  });

  const inviteEmployeesMutation = useMutation({
    mutationFn: async (employees: { name: string; email: string }[]) => {
      const response = await apiRequest("POST", "/api/small-business/invite-employees", {
        employees,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.smallBusinessDashboard });
      setIsInviteOpen(false);
      setInviteRows([
        { name: "", email: "" },
        { name: "", email: "" },
        { name: "", email: "" },
      ]);
      toast({
        title: "Invitations Sent",
        description: "Your employees have been invited.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to invite employees",
        variant: "destructive",
      });
    },
  });

  const fundEmployeeMutation = useMutation({
    mutationFn: async ({ employeeId, amountCents }: { employeeId: string; amountCents: number }) => {
      const response = await apiRequest("POST", `/api/small-business/fund-employee/${employeeId}`, {
        amountCents,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.smallBusinessDashboard });
      setIsFundEmployeeOpen(false);
      setFundEmployeeId(null);
      setFundAmountDollars("");
      toast({
        title: "Employee Funded",
        description: "The contribution has been sent.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to fund employee",
        variant: "destructive",
      });
    },
  });

  const handleCopyCode = () => {
    if (data?.partner.companyCode) {
      navigator.clipboard.writeText(data.partner.companyCode);
      setCodeCopied(true);
      toast({ title: "Copied", description: "Company code copied to clipboard." });
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const handleInviteSubmit = () => {
    const validRows = inviteRows.filter((r) => r.name.trim() && r.email.trim());
    if (validRows.length === 0) {
      toast({
        title: "Error",
        description: "Please add at least one employee with name and email.",
        variant: "destructive",
      });
      return;
    }
    inviteEmployeesMutation.mutate(validRows);
  };

  const handleFundEmployee = () => {
    if (!fundEmployeeId || !fundAmountDollars) return;
    const amountCents = Math.round(parseFloat(fundAmountDollars) * 100);
    if (isNaN(amountCents) || amountCents <= 0) {
      toast({
        title: "Error",
        description: "Please enter a valid amount.",
        variant: "destructive",
      });
      return;
    }
    fundEmployeeMutation.mutate({ employeeId: fundEmployeeId, amountCents });
  };

  const updateInviteRow = (index: number, field: "name" | "email", value: string) => {
    setInviteRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-6 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const partner = data?.partner;
  const stats = data?.stats;
  const employees = data?.employees ?? [];
  const recentContributions = data?.recentContributions ?? [];

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Building2 className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-bold" data-testid="text-page-title">
              Small Business Health Benefits
            </h1>
            {partner && (
              <p className="text-sm text-muted-foreground" data-testid="text-company-name">
                {partner.companyName}
              </p>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            logoutMutation.mutate();
          }}
          disabled={logoutMutation.isPending}
          data-testid="button-logout"
        >
          <LogOut className="h-4 w-4 mr-2" />
          {logoutMutation.isPending ? "Logging out..." : "Log Out"}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="employees" data-testid="tab-employees">Employees</TabsTrigger>
          <TabsTrigger value="contributions" data-testid="tab-contributions">Contributions</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {partner && (
            <div className="space-y-2">
              <h2 className="text-xl font-semibold" data-testid="text-welcome">
                Welcome, {partner.companyName}
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-muted-foreground">Your company code:</span>
                <code
                  className="px-2 py-1 rounded-md bg-muted font-mono text-sm"
                  data-testid="text-company-code"
                >
                  {partner.companyCode}
                </code>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={handleCopyCode}
                  data-testid="button-copy-code"
                >
                  {codeCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          )}

          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Employees</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="text-total-employees">
                    {stats.enrolledEmployees + stats.invitedEmployees}
                  </div>
                  {stats.invitedEmployees > 0 && (
                    <Badge variant="secondary" className="mt-1" data-testid="badge-invited-count">
                      {stats.invitedEmployees} invited
                    </Badge>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">This Month</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="text-this-month">
                    {formatCents(stats.thisMonthContributed)}
                  </div>
                  <p className="text-xs text-muted-foreground">Contributions this month</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Contributed</CardTitle>
                  <Wallet className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="text-total-contributed">
                    {formatCents(stats.totalContributed)}
                  </div>
                  <p className="text-xs text-muted-foreground">All time</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Per Employee</CardTitle>
                  <Heart className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="text-per-employee">
                    {formatCents(stats.monthlyPerEmployeeCents)}
                  </div>
                  <p className="text-xs text-muted-foreground">Monthly per employee</p>
                </CardContent>
              </Card>
            </div>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col sm:flex-row gap-3 flex-wrap">
              <Button
                variant="outline"
                onClick={() => setActiveTab("employees")}
                data-testid="button-invite-employees"
              >
                <Send className="h-4 w-4 mr-2" />
                Invite Employees
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>

              <Dialog open={isFundAllOpen} onOpenChange={setIsFundAllOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="button-fund-all">
                    <DollarSign className="h-4 w-4 mr-2" />
                    Fund All Employees
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Fund All Employees</DialogTitle>
                    <DialogDescription>
                      This will send {partner ? formatCents(partner.monthlyPerEmployeeCents) : "$0.00"} to
                      each enrolled employee's HealthWallet.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsFundAllOpen(false)} data-testid="button-cancel-fund-all">
                      Cancel
                    </Button>
                    <Button
                      onClick={() => fundAllMutation.mutate()}
                      disabled={fundAllMutation.isPending}
                      data-testid="button-confirm-fund-all"
                    >
                      {fundAllMutation.isPending ? "Processing..." : "Confirm & Fund"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>How It Works</CardTitle>
              <CardDescription>Get started with employee health benefits in 3 simple steps</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex flex-col items-center text-center gap-2">
                  <div className="flex items-center justify-center h-10 w-10 rounded-full bg-muted text-muted-foreground font-bold">
                    1
                  </div>
                  <p className="text-sm font-medium" data-testid="text-step-1">
                    Share your company code with employees
                  </p>
                </div>
                <div className="flex flex-col items-center text-center gap-2">
                  <div className="flex items-center justify-center h-10 w-10 rounded-full bg-muted text-muted-foreground font-bold">
                    2
                  </div>
                  <p className="text-sm font-medium" data-testid="text-step-2">
                    Employees create a HealthWallet account and enter the code
                  </p>
                </div>
                <div className="flex flex-col items-center text-center gap-2">
                  <div className="flex items-center justify-center h-10 w-10 rounded-full bg-muted text-muted-foreground font-bold">
                    3
                  </div>
                  <p className="text-sm font-medium" data-testid="text-step-3">
                    Fund their health wallets monthly - they use it for doctor visits, prescriptions, and emergencies
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="employees" className="space-y-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h2 className="text-lg font-semibold">Employee Roster</h2>
            <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
              <DialogTrigger asChild>
                <Button data-testid="button-open-invite-dialog">
                  <Plus className="h-4 w-4 mr-2" />
                  Invite Employees
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Invite Employees</DialogTitle>
                  <DialogDescription>
                    Add employee names and emails to send invitations.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {inviteRows.map((row, index) => (
                    <div key={index} className="flex items-end gap-2">
                      <div className="flex-1">
                        <Label htmlFor={`invite-name-${index}`} className="text-xs">Name</Label>
                        <Input
                          id={`invite-name-${index}`}
                          placeholder="Full name"
                          value={row.name}
                          onChange={(e) => updateInviteRow(index, "name", e.target.value)}
                          data-testid={`input-invite-name-${index}`}
                        />
                      </div>
                      <div className="flex-1">
                        <Label htmlFor={`invite-email-${index}`} className="text-xs">Email</Label>
                        <Input
                          id={`invite-email-${index}`}
                          type="email"
                          placeholder="email@example.com"
                          value={row.email}
                          onChange={(e) => updateInviteRow(index, "email", e.target.value)}
                          data-testid={`input-invite-email-${index}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  onClick={() => setInviteRows((prev) => [...prev, { name: "", email: "" }])}
                  data-testid="button-add-invite-row"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Another
                </Button>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsInviteOpen(false)} data-testid="button-cancel-invite">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleInviteSubmit}
                    disabled={inviteEmployeesMutation.isPending}
                    data-testid="button-submit-invite"
                  >
                    {inviteEmployeesMutation.isPending ? "Sending..." : "Send Invitations"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {employees.length > 0 ? (
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Wallet</TableHead>
                      <TableHead className="text-right">Total Received</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.map((employee) => (
                      <TableRow key={employee.id} data-testid={`employee-row-${employee.id}`}>
                        <TableCell className="font-medium" data-testid={`text-employee-name-${employee.id}`}>
                          {employee.employeeName}
                        </TableCell>
                        <TableCell className="text-muted-foreground" data-testid={`text-employee-email-${employee.id}`}>
                          {employee.employeeEmail}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={employee.status === "enrolled" ? "default" : "secondary"}
                            data-testid={`badge-status-${employee.id}`}
                          >
                            {employee.status}
                          </Badge>
                        </TableCell>
                        <TableCell data-testid={`icon-wallet-${employee.id}`}>
                          {employee.walletId ? (
                            <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                          ) : (
                            <X className="h-4 w-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell className="text-right" data-testid={`text-received-${employee.id}`}>
                          {formatCents(employee.totalReceivedCents)}
                        </TableCell>
                        <TableCell>
                          {employee.status === "enrolled" && employee.walletId && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setFundEmployeeId(employee.id);
                                setFundAmountDollars("");
                                setIsFundEmployeeOpen(true);
                              }}
                              data-testid={`button-fund-${employee.id}`}
                            >
                              <DollarSign className="h-3 w-3 mr-1" />
                              Fund
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground" data-testid="text-empty-employees">
                  No employees yet. Invite your team to get started.
                </p>
              </CardContent>
            </Card>
          )}

          <Dialog open={isFundEmployeeOpen} onOpenChange={setIsFundEmployeeOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Fund Employee</DialogTitle>
                <DialogDescription>
                  Enter the amount to send to this employee's HealthWallet.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <Label htmlFor="fund-amount">Amount (USD)</Label>
                <Input
                  id="fund-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                  value={fundAmountDollars}
                  onChange={(e) => setFundAmountDollars(e.target.value)}
                  data-testid="input-fund-amount"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsFundEmployeeOpen(false)} data-testid="button-cancel-fund-employee">
                  Cancel
                </Button>
                <Button
                  onClick={handleFundEmployee}
                  disabled={fundEmployeeMutation.isPending}
                  data-testid="button-confirm-fund-employee"
                >
                  {fundEmployeeMutation.isPending ? "Sending..." : "Send Funds"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="contributions" className="space-y-6">
          {recentContributions.length > 0 && (
            <Card>
              <CardContent className="py-3">
                <p className="text-sm font-medium" data-testid="text-contributions-summary">
                  Total: {formatCents(recentContributions.reduce((sum, c) => sum + c.amountCents, 0))} across{" "}
                  {recentContributions.length} contribution{recentContributions.length !== 1 ? "s" : ""}
                </p>
              </CardContent>
            </Card>
          )}

          {recentContributions.length > 0 ? (
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Type</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentContributions.map((contribution) => (
                      <TableRow key={contribution.id} data-testid={`contribution-row-${contribution.id}`}>
                        <TableCell className="text-muted-foreground" data-testid={`text-contribution-date-${contribution.id}`}>
                          {format(new Date(contribution.createdAt), "MMM d, yyyy")}
                        </TableCell>
                        <TableCell data-testid={`text-contribution-desc-${contribution.id}`}>
                          {contribution.description || "Contribution"}
                        </TableCell>
                        <TableCell className="text-right font-medium" data-testid={`text-contribution-amount-${contribution.id}`}>
                          {formatCents(contribution.amountCents)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" data-testid={`badge-contribution-type-${contribution.id}`}>
                            {contribution.type}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <DollarSign className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground" data-testid="text-empty-contributions">
                  No contributions yet. Fund your employees to see contribution history here.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
